import { ReactNode } from 'react';

import { Endpoints } from '@/api';
import {
  FilterCheckboxItem,
  FilterDivider,
  FilterItemNoResults,
  FilterSearch,
  FiltersList,
  FiltersSubMenu,
} from '@/design_system/Filters/FiltersDrawer/FiltersDrawer';

import { useEntityFilterState } from './useEntityFilter';

const MINIMUM_ENTITIES_FOR_SEARCH = 5;

export const MobileEntityFilter = ({
  filterId,
  selectedEntities,
  setSelectedEntities,
  includeGroups = true,
  traits,
  label,
  searchPlaceholder,
  icon,
}: {
  filterId: string;
  selectedEntities: { id: string; name: string }[];
  setSelectedEntities: (entities: { id: string; name: string }[]) => void;
  includeGroups?: boolean;
  traits?: Endpoints['GET /entities-and-groups']['query']['traits'];
  label: string;
  searchPlaceholder: string;
  icon: ReactNode;
}) => {
  const { search, setSearch, fetchNextPage, isLoading, entityGroups, entities, count } =
    useEntityFilterState({ traits });

  const selectedEntitiesIds = selectedEntities.map(({ id }) => id);

  const options: ReactNode[] = [];

  if (includeGroups && entityGroups.length) {
    options.push(
      ...entityGroups.map((entityGroup) => (
        <FilterCheckboxItem key={entityGroup.id} id={entityGroup.id} label={entityGroup.name} />
      ))
    );
  }

  if (includeGroups && entityGroups.length && entities.length) {
    options.push(<FilterDivider key="divider" />);
  }

  if (entities.length) {
    options.push(
      ...entities.map((entity) => (
        <FilterCheckboxItem key={entity.id} id={entity.id} label={entity.name} />
      ))
    );
  }

  if (options.length === 0) {
    options.push(<FilterItemNoResults key="empty-mobile" />);
  }

  const toggleEntity = (entityId: string) => {
    const entity = [...entityGroups, ...entities].find(({ id }) => id === entityId);

    if (entity) {
      if (selectedEntitiesIds.includes(entity.id)) {
        setSelectedEntities(selectedEntities.filter(({ id }) => id !== entity.id));
      } else {
        setSelectedEntities([...selectedEntities, entity]);
      }
    }
  };

  const optionsWithSearch = (
    <FilterSearch
      ariaLabel={label}
      inputProps={{
        ariaLabel: searchPlaceholder,
        placeholder: searchPlaceholder,
        value: search,
        onChange: setSearch,
      }}
      onLoadMore={fetchNextPage}
      isLoading={entityGroups.length === 0 && entities.length === 0 && isLoading}
      isLoadingMore={isLoading}
      values={selectedEntitiesIds}
      onChange={toggleEntity}
    >
      {options}
    </FilterSearch>
  );

  const optionsWithoutSearch = (
    <FiltersList ariaLabel={label} values={selectedEntitiesIds} onChange={toggleEntity}>
      {options}
    </FiltersList>
  );

  return (
    <FiltersSubMenu id={filterId} icon={icon} label={label} count={selectedEntities.length}>
      {count < MINIMUM_ENTITIES_FOR_SEARCH ? optionsWithoutSearch : optionsWithSearch}
    </FiltersSubMenu>
  );
};
